/* Shut Off Valves finder — guided gate-valve selector.
 * Steps: Brand (Kennedy / Wilkins) -> Size -> matching valves.
 * Data-driven from the storefront shutoff-valves catalog. Gate/stem type
 * (OS&Y / NRS) comes from the product `gate:` tags, exposed by the app API as
 * `product.gates` (["OS&Y"] / ["NRS"]). It used to be a step of its own; now
 * every gate-tagged valve in the chosen size is listed together, OS&Y first,
 * with the type printed on the card. Results dispatch `quote:add` to the
 * global quote drawer, same as the repair-parts finder. Only Kennedy &
 * Wilkins are offered (per store spec).
 */
if (!customElements.get('shutoff-finder')) {
  // Only these two manufacturers, in this display order.
  const BRANDS = [
    { key: 'kennedy', label: 'Kennedy' },
    { key: 'wilkins', label: 'Wilkins' },
  ];
  const BRAND_KEYS = BRANDS.map((b) => b.key);
  // Results list order by gate type: rising stem first, then NRS.
  const GATE_ORDER = ['OS&Y', 'NRS'];
  const gateRank = (gate) => {
    const i = GATE_ORDER.indexOf(gate);
    return i === -1 ? GATE_ORDER.length : i;
  };

  class ShutoffFinder extends HTMLElement {
    constructor() {
      super();
      this.rows = []; // [{ brandKey, brand, size, gate, product, variant }]
      this.state = { brand: null, size: null };
    }

    connectedCallback() {
      this.apiBase = (this.dataset.apiBase || 'https://app.thebackflowdepot.com').replace(/\/$/, '');
      this.brandLogos = { kennedy: (this.dataset.kennedyLogo || '').trim(), wilkins: (this.dataset.wilkinsLogo || '').trim() };
      this.innerHTML = '<p class="repair-parts-finder__loading text-center">Loading shut-off valves…</p>';
      this.load();
    }

    async load() {
      try {
        const res = await fetch(`${this.apiBase}/api/storefront/parts-finder/products?scope=shutoff-valves&category=shutoff-valves&limit=100`);
        const data = await res.json();
        this.build(data.products || []);
        this.render();
      } catch (e) {
        this.innerHTML = '<p class="repair-parts-finder__loading text-center">Couldn’t load valves. Please call (916) 251-7071 and we’ll help you find the right shut-off valve.</p>';
      }
    }

    // Flatten each Kennedy/Wilkins valve into (size × gate) rows so the funnel
    // can offer sizes for a brand, then list every valve in that size. The
    // `gate:` tag is still required even though it is no longer a step: it is
    // what separates the gate valves from the inlet/outlet ball valves and
    // outlet fittings that share the shutoff-valves category.
    build(products) {
      this.rows = [];
      for (const p of products) {
        const brandKey = (p.brand || '').toLowerCase();
        if (!BRAND_KEYS.includes(brandKey)) continue; // Kennedy / Wilkins only
        const sizes = p.sizes || [];
        const gates = p.gates || [];
        if (!sizes.length || !gates.length) continue; // needs both to be findable
        const variant = (p.variants || [])[0];
        if (!variant || !variant.variantId) continue;
        for (const size of sizes) {
          for (const gate of gates) {
            this.rows.push({ brandKey, brand: p.brand, size, gate, product: p, variant });
          }
        }
      }
    }

    brandsAvail() {
      const have = new Set(this.rows.map((r) => r.brandKey));
      return BRANDS.filter((b) => have.has(b.key));
    }
    sizesFor(brandKey) {
      const set = new Set(this.rows.filter((r) => r.brandKey === brandKey).map((r) => r.size));
      return [...set].sort((a, b) => parseFloat(a) - parseFloat(b) || String(a).localeCompare(String(b)));
    }
    resultsFor(brandKey, size) {
      const seen = new Set();
      const out = [];
      for (const r of this.rows) {
        if (r.brandKey !== brandKey || r.size !== size) continue;
        if (seen.has(r.variant.variantId)) continue;
        seen.add(r.variant.variantId);
        out.push(r);
      }
      // OS&Y before NRS, then by title, so the list reads the same every time.
      return out.sort((a, b) =>
        gateRank(a.gate) - gateRank(b.gate) || String(a.product.title).localeCompare(String(b.product.title))
      );
    }

    setState(patch) { Object.assign(this.state, patch); this.render(); }

    /** Sizes as fractions (1 ½", not 1.5") — display only; filtering keeps the
        API's value. Mirrors the app's src/lib/size-display.ts. Null = not a size. */
    fractionSize(size) {
      const s = String(size).replace(/["”″]/g, '').trim();
      let v = null;
      const mixed = s.match(/^(\d+)[\s-]+(\d+)\/(\d+)$/);
      const frac = s.match(/^(\d+)\/(\d+)$/);
      if (mixed && Number(mixed[3])) v = Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
      else if (frac && Number(frac[2])) v = Number(frac[1]) / Number(frac[2]);
      else if (/^(\d+\.?\d*|\.\d+)$/.test(s)) v = Number(s);
      if (v === null || !Number.isFinite(v)) return null;
      const n = Math.round(v * 16);
      if (Math.abs(v * 16 - n) > 0.05) return s;
      const whole = Math.floor(n / 16);
      const rest = n % 16;
      if (!rest) return String(whole);
      const gcd = (a, b) => (b ? gcd(b, a % b) : a);
      const d = gcd(rest, 16);
      const key = `${rest / d}/${16 / d}`;
      const glyph = { '1/2': '½', '1/4': '¼', '3/4': '¾', '1/8': '⅛', '3/8': '⅜', '5/8': '⅝', '7/8': '⅞' }[key] || key;
      return whole ? `${whole} ${glyph}` : glyph;
    }

    // Whole, decimal, fraction and mixed sizes (2, 2.5, 1/2, 2 1/2) all read as fractions with the inch mark.
    sizeLabel(s) {
      const f = this.fractionSize(s);
      return f === null ? this.esc(String(s)) : `${this.esc(f)}"`;
    }

    countFor(brandKey, size) { return this.resultsFor(brandKey, size).length; }

    /* ---- Render ----
       Same markup and classes as the repair parts finder (repair-parts-finder.css):
       sticky step bar, one stage card at a time, then the product-card grid. */
    render() {
      const s = this.state;
      let step = 1;
      if (s.brand) step = 2;
      if (s.brand && s.size) step = 3;
      const brandLabel = (BRANDS.find((b) => b.key === s.brand) || {}).label || '';

      const sep = '<span class="repair-parts-finder__trail-sep" aria-hidden="true">&rsaquo;</span>';
      const pill = (n, label, value, reachable) =>
        `<button type="button" class="repair-parts-finder__pill${step === n ? ' is-active' : ''}${value ? ' is-set' : ''}" data-goto="${n}"${reachable ? '' : ' disabled'}>${n}. ${label}${value ? `<span>: ${value}</span>` : ''}</button>`;
      const count = step === 3 ? `<span> (${this.countFor(s.brand, s.size)})</span>` : '';
      const pills = [
        pill(1, 'Brand', brandLabel, true),
        pill(2, 'Size', s.size ? this.sizeLabel(s.size) : '', !!s.brand),
        `<span class="repair-parts-finder__pill repair-parts-finder__pill--static${step === 3 ? ' is-active' : ''}">3. Valves${count}</span>`,
      ].join(sep);

      let body = '';
      if (step === 1) {
        const brands = this.brandsAvail();
        body = brands.length
          ? `<div class="repair-parts-finder__stage-card">
              <div class="repair-parts-finder__stage-heading">
                <h2>Choose the manufacturer</h2>
                <p>Kennedy and Wilkins gate valves, OS&amp;Y and NRS.</p>
              </div>
              <div class="repair-parts-finder__brand-grid">${brands.map((b) => {
                const logo = this.brandLogos[b.key];
                const head = logo
                  ? `<img class="repair-parts-finder__brand-logo" src="${this.esc(logo)}" alt="${this.esc(b.label)}" loading="lazy">`
                  : `<span class="repair-parts-finder__brand-fallback" aria-hidden="true">${this.esc(b.label.slice(0, 3))}</span>`;
                return `<button type="button" class="repair-parts-finder__brand-card" data-brand="${b.key}">${head}<span class="repair-parts-finder__brand-name">${this.esc(b.label)}</span><span class="repair-parts-finder__tile-desc">Gate valves — OS&amp;Y &amp; NRS</span></button>`;
              }).join('')}</div>
            </div>`
          : '<p class="repair-parts-finder__empty-message">No shut-off valves found. Please call <strong>(916) 251-7071</strong>.</p>';
      } else if (step === 2) {
        const sizes = this.sizesFor(s.brand);
        body = `<div class="repair-parts-finder__stage-card">
            <div class="repair-parts-finder__stage-heading repair-parts-finder__stage-heading--split">
              <div>
                <h2>What size <em>${this.esc(brandLabel)}</em> valve?</h2>
                <p>The pipe size, in inches.</p>
              </div>
              <button type="button" class="repair-parts-finder__back-link" data-goto="1">Change brand</button>
            </div>
            <div class="repair-parts-finder__size-row">${sizes.map((sz) => {
              const n = this.countFor(s.brand, sz);
              return `<button type="button" class="repair-parts-finder__size-btn" data-size="${this.esc(sz)}">${this.sizeLabel(sz)}<small>${n}</small></button>`;
            }).join('')}</div>
          </div>`;
      } else {
        const results = this.resultsFor(s.brand, s.size);
        const list = results.length
          ? `<ul class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-theme gap-y-8 repair-parts-finder__grid" role="list">${results.map((r) => this.renderCard(r, brandLabel)).join('')}</ul>`
          : `<p class="repair-parts-finder__empty-message">No exact match in stock. We can still source it — <a href="/pages/contact">request a quote</a> or call <strong>(916) 251-7071</strong>.</p>`;
        body = `<div class="repair-parts-finder__results" aria-live="polite">
            <div class="repair-parts-finder__results-header">
              <div>
                <p class="repair-parts-finder__results-kicker">Shut-off valves</p>
                <h2 class="repair-parts-finder__results-title h4">${this.sizeLabel(s.size)} ${this.esc(brandLabel)} gate valves</h2>
                <p class="repair-parts-finder__results-meta">${results.length} valve${results.length === 1 ? '' : 's'} · OS&amp;Y first, then NRS</p>
              </div>
            </div>
            ${list}
          </div>`;
      }

      const tools = step > 1
        ? '<div class="repair-parts-finder__tools"><button type="button" class="repair-parts-finder__reset" data-goto="1">Start over</button></div>'
        : '';

      this.innerHTML = `
        <div class="repair-parts-finder__toolbar">
          <div class="repair-parts-finder__trail" role="group" aria-label="Your valve">${pills}</div>
          ${tools}
        </div>
        ${body}
        <p class="repair-parts-finder__help">Not sure? <a href="/collections/shut-off-valves">Browse all shut-off valves</a> or call <strong>(916) 251-7071</strong>.</p>`;
      this.bind();
    }

    renderCard(r, brandLabel) {
      const p = r.product;
      // Whether a price shows is a TAG decision, not a size one.
      //
      // `add-to-quote` means never show a price; `purchasable-now` means
      // show it. The app resolves those two tags into `quoteOnly` (see
      // src/lib/purchase-mode.ts) and the repair-parts finder already
      // reads it, so staff can flip a product from Shopify without a theme
      // deploy. Untagged falls back to quote: not showing a price is the safe
      // direction to be wrong in.
      //
      // Kennedy keeps its own wording regardless — those ship by freight,
      // and "call for freight pricing" tells a tester something that
      // "quote on request" does not.
      const isKennedy = r.brandKey === 'kennedy';
      const quoteOnly = isKennedy || p.quoteOnly !== false;
      const priceText = isKennedy
        ? 'Call for freight pricing'
        : quoteOnly
          ? 'Quote on request'
          : (p.priceMin > 0 ? `$${Number(p.priceMin).toFixed(2)}` : 'Quote on request');
      const priceClass = quoteOnly || priceText === 'Quote on request' ? ' repair-parts-finder__card-price--quote' : '';
      const url = p.handle ? `/products/${this.esc(p.handle)}` : '#';
      const image = p.imageUrl
        ? `<img src="${this.esc(p.imageUrl)}" alt="${this.esc(p.title)}" loading="lazy">`
        : '<div class="repair-parts-finder__card-placeholder" aria-hidden="true"></div>';
      const sku = r.variant.sku ? `<p class="repair-parts-finder__card-sku">SKU: ${this.esc(r.variant.sku)}</p>` : '';
      // The gate type is a chip on the card (it used to be a step of its own).
      const chips = [this.esc(brandLabel), this.sizeLabel(r.size), ...(p.gates || []).map((g) => this.esc(g))].filter(Boolean);
      return `<li class="repair-parts-finder__item">
          <div class="repair-parts-finder__card">
            <a class="repair-parts-finder__card-main" href="${url}">
              <div class="repair-parts-finder__card-image">${image}</div>
              <div class="repair-parts-finder__card-content">
                ${sku}
                <h3 class="repair-parts-finder__card-title">${this.esc(p.title)}</h3>
                <div class="repair-parts-finder__chips">${chips.map((c) => `<span>${c}</span>`).join('')}</div>
                <p class="repair-parts-finder__card-price${priceClass}">${priceText}</p>
              </div>
            </a>
            <div class="repair-parts-finder__card-actions">
              <button type="button" class="btn btn--primary w-full js-add-to-quote" data-variant-id="${this.esc(r.variant.variantId)}" data-title="${this.esc(p.title)}" data-sku="${this.esc(r.variant.sku || '')}" data-image="${this.esc(p.imageUrl || '')}" data-handle="${this.esc(p.handle || '')}">Add to quote</button>
              <a class="repair-parts-finder__card-link" href="${url}">View product</a>
            </div>
          </div>
        </li>`;
    }

    bind() {
      this.querySelectorAll('[data-brand]').forEach((el) => el.addEventListener('click', () => this.setState({ brand: el.dataset.brand, size: null })));
      this.querySelectorAll('[data-size]').forEach((el) => el.addEventListener('click', () => this.setState({ size: el.dataset.size })));
      this.querySelectorAll('[data-goto]:not([disabled])').forEach((el) => el.addEventListener('click', () => {
        const n = Number(el.dataset.goto);
        if (n === 1) this.setState({ brand: null, size: null });
        else if (n === 2) this.setState({ size: null });
      }));
      // Results add-to-quote → global quote drawer (same contract as the finder).
      this.querySelectorAll('.js-add-to-quote').forEach((btn) => btn.addEventListener('click', (event) => {
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('quote:add', { detail: {
          variantId: btn.dataset.variantId, title: btn.dataset.title,
          sku: btn.dataset.sku || null, image: btn.dataset.image || null,
          handle: btn.dataset.handle || null, quantity: 1,
        } }));
      }));
    }

    esc(s) { return String(s ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  }
  customElements.define('shutoff-finder', ShutoffFinder);
}
